import { ViewConfig } from "@lib/views";
import {
    ChartOfAccountsColumn,
    ChartOfAccountsView,
    ChartOfAccountsViewSchema,
} from "@common/types/views/chartOfAccountsView";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { ChartOfAccountsRow } from "@components/accounting/chartOfAccounts/utils";
import { createChartOfAccountsViewConfig } from "@components/accounting/chartOfAccounts/chartOfAccountsViewConfig";
import { ApplyCoaPreviewNameCell } from "./cells/ApplyCoaPreviewNameCell";
import { ApplyCoaPreviewOriginCell } from "./cells/ApplyCoaPreviewOriginCell";

// Read-only preview view config: reuses the live CoA columns/cells but swaps the
// NAME cell for the preview variant (Added / Reassigned / Moved / Archived tags)
// and drops the Entities / Actions columns (single-entity, no editing). The
// Source column stays so each row shows Custom / Kick like the live view.
interface PreviewViewOptions {
    // Only the multi-entity apply flow surfaces the Origin column (it needs the
    // active entity's identity). The AddClient setup preview omits it.
    showOriginColumn: boolean;
}

export function createApplyCoaPreviewViewConfig({
    showOriginColumn,
}: PreviewViewOptions): ViewConfig<
    ChartOfAccountsRow,
    ChartOfAccountsColumn,
    never
> {
    const config = createChartOfAccountsViewConfig();
    return {
        ...config,
        columns: {
            ...config.columns,
            [ChartOfAccountsColumn.NAME]: {
                ...config.columns[ChartOfAccountsColumn.NAME],
                cell: ApplyCoaPreviewNameCell,
            },
            // Preview-only column: where each account starts (entity / template
            // / both). Only meaningful in the apply flow; falls back to the
            // shared `internal` placeholder otherwise so it never renders.
            [ChartOfAccountsColumn.ORIGIN]: showOriginColumn
                ? {
                      accessorKey: "account.id",
                      name: "Origin",
                      header: "Origin",
                      cell: ApplyCoaPreviewOriginCell,
                      minSize: 180,
                      maxSize: 320,
                  }
                : config.columns[ChartOfAccountsColumn.ORIGIN],
            [ChartOfAccountsColumn.TYPE]: {
                ...config.columns[ChartOfAccountsColumn.TYPE],
                hiddenFromSettings: true,
            },
            [ChartOfAccountsColumn.SUBTYPE]: {
                ...config.columns[ChartOfAccountsColumn.SUBTYPE],
                hiddenFromSettings: true,
            },
            [ChartOfAccountsColumn.FORM_1099_BOX]: {
                ...config.columns[ChartOfAccountsColumn.FORM_1099_BOX],
                hiddenFromSettings: true,
            },
        },
    };
}

export function buildApplyCoaPreviewDefaultView({
    showOriginColumn,
}: PreviewViewOptions): ChartOfAccountsView {
    return {
        type: ViewType.CHART_OF_ACCOUNTS,
        filters: {},
        columns: [
            { id: ChartOfAccountsColumn.NAME, options: { pin: "left" } },
            { id: ChartOfAccountsColumn.SOURCE },
            ...(showOriginColumn ? [{ id: ChartOfAccountsColumn.ORIGIN }] : []),
        ],
        options: {
            density: ViewDensity.LG,
        },
    };
}

export function resolveChartOfAccountsView(
    candidate: View,
    current: ChartOfAccountsView,
): ChartOfAccountsView {
    const result = ChartOfAccountsViewSchema.safeParse(candidate);
    return result.success ? result.data : current;
}
